<template>
  <div class="flex items-center mb-1">
    <div class="flex items-center gap-2">
      <p class="m-0 font-bold text-mouse">
        {{ t('news/social/title') }}
      </p>

      <!-- Copy Link Button -->
      <button
        type="button"
        :title="t('news/social/link')"
        :aria-label="isCopied ? t('news/social/copy') : t('news/social/link')"
        :disabled="isCopied"
        class="flex items-center justify-center size-10 rounded-full text-brand transition-colors hover:text-mouse"
        @click="copyToClipboard"
      >
        <fa-icon v-if="isCopied" :icon="faCheck" class="size-4" />
        <fa-icon v-else :icon="faCopy" class="size-4" />
      </button>

      <!-- Social Share Options -->
      <a
        v-for="option in shareOptions"
        :key="option.platform"
        :href="option.url"
        target="_blank"
        rel="noopener noreferrer"
        :title="option.title"
        :aria-label="option.title"
        class="flex items-center justify-center size-10 rounded-full text-brand transition-colors hover:text-mouse"
      >
        <fa-icon :icon="option.icon" aria-hidden="true" class="size-4" />
      </a>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  faFacebook,
  faReddit,
  faWhatsapp,
  faXTwitter,
} from '@fortawesome/free-brands-svg-icons';
import { faCheck, faCopy, faEnvelope } from '@fortawesome/pro-solid-svg-icons';
import { computed, ref } from 'vue';

const { t } = useI18n();
const config = useRuntimeConfig();
const route = useRoute();
const isCopied = ref(false);

const props = withDefaults(
  defineProps<{
    shareText?: string;
    shareUrl?: string;
  }>(),
  {
    shareText: '',
    shareUrl: '',
  },
);

const resolvedShareUrl = computed(
  () => props.shareUrl || `${config.public.websiteUrl}${route.fullPath}`,
);

const shareOptions = computed(() => {
  const encodedUrl = encodeURIComponent(resolvedShareUrl.value);
  const encodedText = encodeURIComponent(props.shareText);

  return [
    {
      platform: 'email',
      title: t('news/social/email'),
      icon: faEnvelope,
      url: `mailto:?subject=${encodedText}&body=${encodedUrl}`,
    },
    {
      platform: 'facebook',
      title: t('news/social/facebook'),
      icon: faFacebook,
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    },
    {
      platform: 'x',
      title: t('news/social/twitter'),
      icon: faXTwitter,
      url: `https://x.com/intent/post?text=${encodedText}&url=${encodedUrl}`,
    },
    {
      platform: 'whatsapp',
      title: t('news/social/whatsapp'),
      icon: faWhatsapp,
      url: `https://wa.me/?text=${encodeURIComponent(
        `${props.shareText} ${resolvedShareUrl.value}`,
      )}`,
    },
    {
      platform: 'reddit',
      title: t('news/social/reddit'),
      icon: faReddit,
      url: `https://www.reddit.com/submit?title=${encodedText}&url=${encodedUrl}`,
    },
  ];
});

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(resolvedShareUrl.value);
    isCopied.value = true;
    setTimeout(() => {
      isCopied.value = false;
    }, 2000);
  } catch (err) {
    console.error('Failed to copy:', err);
  }
};
</script>
